import {computed, type ComputedRef, type Ref, ref} from "vue";
import {defineStore} from "pinia";
import axios, {AxiosResponse} from "axios";
import {
    Category,
    GridClass, Media,
    Product,
    ProductRequest,
    ProductResponse, SearchMetadata,
    SortDirection,
    SortField
} from "../utils/types";
import {useCategoryStore} from "./category.ts";

const BASE_URL =
    "https://7m46tnf5wd.execute-api.ap-southeast-2.amazonaws.com/prod/api/search/geo";

export const useProductStore = defineStore("product-store", () => {
    const {
        years,
        altitude,
        media,
        specifics,
        searchPage
    } = useCategoryStore()
    const perPage = (window as any).pageLimit as number
    const lat: Ref<number> = ref(0)
    const lng: Ref<number> = ref(0)
    const radius: Ref<number> = ref(0)

    setCords()
    const total: Ref<number> = ref(0)
    const isLoading: Ref<boolean> = ref(false)
    const gridClass: Ref<GridClass> = ref(GridClass.LIST)
    const initialRequest: Ref<ProductRequest> = ref({
        lat: lat,
        lng: lng,
        perPage: perPage,
        radius: radius,
        page: 1,
        sort: [[SortField.CREATED_AT, SortDirection.DESC], [SortField.DISTANCE, SortDirection.ASC]]
    });

    const prodRequest: Ref<ProductRequest> = ref({
        lat: lat,
        lng: lng,
        perPage: perPage,
        page: 1,
        radius: radius,
        sort: [[SortField.CREATED_AT, SortDirection.DESC], [SortField.DISTANCE, SortDirection.ASC]]
    });

    const products: Ref<Product[]> = ref([])
    const appliedFilters: Ref<SearchMetadata | null> = ref(null)

    const maxPage: ComputedRef<number> = computed(() => {
        return Math.ceil(total.value / perPage);
    });

    const isFilterActive: ComputedRef<boolean> = computed(() => {
        return selectedCount.value > 0
    });

    const selectedCount: ComputedRef<number> = computed(() => {
       return selectedYears.value + selectedAltitude.value + selectedMedia.value + selectedSpecific.value
    });

    const selectedYears: ComputedRef<number> = computed(() => {
        if (searchPage) {
            return prodRequest.value.year ? prodRequest.value.year?.length : 0;
        } else {
            return years ? years.filter(item => item.selected === '1').length : 0
        }

    });

    const selectedAltitude: ComputedRef<number> = computed(() => {
        if (searchPage) {
            return [!!prodRequest.value.altitudeMin || !!prodRequest.value.altitudeMax].filter(Boolean).length;
        } else {
            return altitude ? altitude.filter(item => item.selected === '1').length : 0
        }

    });

    const selectedMedia: ComputedRef<number> = computed(() => {
        if (searchPage) {
            if (!prodRequest.value.media) {
                return 0
            }
            if (prodRequest.value.media === Media.ALL) {
                return 2
            }
            return 1
        } else {
            return media ? media.filter(item => item.selected === '1').length : 0
        }

    });

    const selectedSpecific: ComputedRef<number> = computed(() => {
        if (searchPage) {
            return [!!prodRequest.value.isNighttime, !!prodRequest.value.isSunrise, !!prodRequest.value.isSunrise].filter(Boolean).length
        } else {
            return specifics ? specifics.filter(item => item.selected === '1').length : 0
        }
    });

    function setInitialRequest(request: ProductRequest) {
        initialRequest.value = request;
        prodRequest.value = request;
    }


    function setCords() {
        const currentUrl = window.location.href
        const url = new URL(currentUrl)
        const params = url.searchParams;
        const initLat = params.get('lat');
        const initLng = params.get('lng');
        if (initLat && initLng) {
            lat.value = +initLat
            lng.value = +initLng
            radius.value = (window as any).addressRadius
        } else {
            lat.value = (window as any).cord.lat
            lng.value = (window as any).cord.lng
            radius.value = (window as any).radius
        }
    }

    function setRequest(request: ProductRequest) {
        prodRequest.value = request;
        getProducts()
    }

    function resetFilters() {
        if (!isFilterActive.value) return;
        if(!searchPage){
            let link = window.location;
            window.location.replace(link.origin + link.pathname);
            return
        }
        prodRequest.value = {...initialRequest.value};
        getProducts();
    }

    function composeUrl() {
        return (
            Object.keys(prodRequest.value).reduce(
                (acc, item) => {
                    const value = item === 'sort' ? JSON.stringify(prodRequest.value[item]) : prodRequest.value[item]
                    return `${acc}${item}=${value}&`
                },
                `${BASE_URL}?`
            )
        );
    }

    async function getProducts() {
        try {
            isLoading.value = true
            const url = composeUrl();
            const response: AxiosResponse<ProductResponse> = await axios.get(url);
            products.value = response.data.data
            appliedFilters.value = response.data.searchMetadata
            total.value = response.data.searchMetadata.itemsTotal
        } catch (e) {
            console.log(e);
        } finally {
            isLoading.value = false
        }
    }

    function deepEqual(obj1: ProductRequest, obj2: ProductRequest) {
        if (obj1 === obj2) {
            return true;
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (let key of keys1) {
            if (!obj2.hasOwnProperty(key) || !deepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    }

    function setGrid(cssClass: GridClass) {
        gridClass.value = cssClass
    }

    function goToLink(category: Category) {
        if (category.selected === '0') {
            window.location.href = category.add
        } else {
            window.location.href = category.remove
        }
    }

    return {
        getProducts,
        resetFilters,
        setInitialRequest,
        setRequest,
        setGrid,
        prodRequest,
        isFilterActive,
        selectedCount,
        selectedYears,
        selectedAltitude,
        initialRequest,
        selectedMedia,
        isLoading,
        gridClass,
        appliedFilters,
        products,
        maxPage,
        selectedSpecific,
        total,
        goToLink
    };
});
